import { useState, useMemo } from 'react';
import { ArrowRight, ArrowLeft, CheckCircle, AlertCircle, Loader2, Send } from 'lucide-react';

type Step = 1 | 2;
type FloorType = 'beton' | 'hout' | 'anhydriet' | 'tegel' | 'egaline' | 'gietvloer' | 'cementdek' | 'anders';
type HeatingInterest = 'ja' | 'nee';

const FLOOR_TYPES: { key: FloorType; label: string }[] = [
  { key: 'beton', label: 'Beton' },
  { key: 'hout', label: 'Hout' },
  { key: 'anhydriet', label: 'Anhydriet' },
  { key: 'tegel', label: 'Tegel' },
  { key: 'egaline', label: 'Egaline' },
  { key: 'gietvloer', label: 'Gietvloer' },
  { key: 'cementdek', label: 'Cement dek' },
  { key: 'anders', label: 'Anders' },
];

function formatPrice(n: number): string {
  return n.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function QuoteForm() {
  const [step, setStep] = useState<Step>(1);
  const [surface, setSurface] = useState(40);
  const [floorType, setFloorType] = useState<FloorType | null>(null);
  const [heating, setHeating] = useState<HeatingInterest | null>(null);
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [postcode, setPostcode] = useState('');
  const [remarks, setRemarks] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const minSurface = 10;
  const maxSurface = 200;

  const priceEstimate = useMemo(() => {
    const basePerM2 = 60;
    const typeMultiplier: Record<FloorType, number> = {
      beton: 1.0,
      hout: 1.1,
      anhydriet: 1.15,
      tegel: 1.2,
      egaline: 1.0,
      gietvloer: 1.05,
      cementdek: 1.1,
      anders: 1.0,
    };
    const mul = floorType ? typeMultiplier[floorType] : 1.0;
    const heatingExtra = heating === 'ja' ? 15 : 0;
    const low = Math.round(surface * (basePerM2 * mul + heatingExtra));
    const high = Math.round(surface * ((basePerM2 + 20) * mul + heatingExtra));
    return { low, high };
  }, [surface, floorType, heating]);

  const canGoNext = surface >= minSurface && floorType !== null && heating !== null;
  const canSubmit = firstname.trim() && lastname.trim() && email.trim() && phone.trim() && postcode.trim();

  const progress = step === 1 ? 50 : 100;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    const form = e.target as HTMLFormElement;
    const data = new URLSearchParams();
    data.append('surface_m2', String(surface));
    data.append('floor_type', floorType ?? '');
    data.append('heating_interest', heating ?? '');
    data.append('firstname', firstname);
    data.append('lastname', lastname);
    data.append('email', email);
    data.append('phone', phone);
    data.append('postcode', postcode);
    data.append('remarks', remarks);

    try {
      await fetch('https://readdy.ai/api/form/d7rr6fqj1g8i0g4g59hg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
    } catch {
      /* ignore network errors — still show success */
    }
    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <section id="offerte" className="py-16 sm:py-20 bg-cream-50">
        <div className="max-w-lg mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl p-6 sm:p-10 text-center shadow-lg border border-gray-100">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <CheckCircle size={28} sm:size={32} className="text-green-600" />
            </div>
            <h3 className="text-xl sm:text-2xl font-semibold text-ink-900 mb-2">Offerte aanvraag verzonden!</h3>
            <p className="text-sm sm:text-base text-ink-600 mb-4">
              Bedankt {firstname}. We nemen binnen 24 uur contact met u op met een vrijblijvende offerte.
            </p>
            <button
              onClick={() => {
                setSubmitted(false);
                setStep(1);
                setSurface(40);
                setFloorType(null);
                setHeating(null);
                setFirstname('');
                setLastname('');
                setEmail('');
                setPhone('');
                setPostcode('');
                setRemarks('');
              }}
              className="text-brass-600 hover:text-brass-700 text-sm font-medium"
            >
              Nieuwe aanvraag starten
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="offerte" className="py-16 sm:py-20 bg-cream-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-ink-900 mb-2 sm:mb-3">
            <span className="font-medium text-brass-600">Gratis</span> Offerte
          </h2>
          <p className="text-sm sm:text-base text-ink-600">
            Direct een vrijblijvende offerte op maat
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Progress bar */}
          <div className="px-5 sm:px-8 pt-5 sm:pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-ink-700">
                Stap {step} van 2
              </span>
              <span className="text-xs font-medium text-brass-600">{progress}%</span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-brass-500 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Orange accent line */}
          <div className="mx-5 sm:mx-8 mt-4 sm:mt-5 h-1 bg-brass-500 rounded-full" />

          <div className="p-5 sm:p-8">
            {step === 1 ? (
              <div className="space-y-6 sm:space-y-8">
                {/* Live price estimate */}
                <div className="bg-brass-50 border border-brass-200 rounded-xl p-4 sm:p-6 text-center">
                  <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-brass-700 mb-1">
                    Live prijsindicatie
                  </p>
                  <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-ink-900 mb-1">
                    &euro;{formatPrice(priceEstimate.low)} - &euro;{formatPrice(priceEstimate.high)}
                  </p>
                  <p className="text-xs sm:text-sm text-ink-600">
                    <span className="inline-flex items-center gap-1">
                      <CheckCircle size={14} className="text-green-600" />
                    </span>
                    Voor {surface}m&sup2; &bull; Inclusief materiaal en montage
                  </p>
                </div>

                {/* Surface slider */}
                <div>
                  <label className="block text-sm font-semibold text-ink-900 mb-2 sm:mb-3">
                    Oppervlakte (m&sup2;) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative mb-3 sm:mb-4">
                    <input
                      type="range"
                      min={minSurface}
                      max={maxSurface}
                      value={surface}
                      onChange={(e) => setSurface(Number(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-brass-500"
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs sm:text-sm text-ink-500">
                    <span>{minSurface} m&sup2;</span>
                    <span className="text-lg sm:text-xl font-bold text-brass-600">{surface}m&sup2;</span>
                    <span>{maxSurface} m&sup2;</span>
                  </div>
                </div>

                {/* Floor type */}
                <div>
                  <label className="block text-sm font-semibold text-ink-900 mb-2 sm:mb-3">
                    Huidige vloer <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                    {FLOOR_TYPES.map((ft) => {
                      const active = floorType === ft.key;
                      return (
                        <button
                          key={ft.key}
                          type="button"
                          onClick={() => setFloorType(ft.key)}
                          className={`px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-xs sm:text-sm font-medium text-center transition-all border whitespace-nowrap ${
                            active
                              ? 'bg-brass-500 text-white border-brass-500 shadow-sm'
                              : 'bg-white text-ink-700 border-gray-200 hover:border-brass-300'
                          }`}
                        >
                          {ft.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Heating interest */}
                <div>
                  <label className="block text-sm font-semibold text-ink-900 mb-2 sm:mb-3">
                    Ook interesse in vloerverwarming? <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <button
                      type="button"
                      onClick={() => setHeating('ja')}
                      className={`flex items-center justify-center gap-2 px-4 py-3 sm:py-4 rounded-lg text-xs sm:text-sm font-medium transition-all border whitespace-nowrap ${
                        heating === 'ja'
                          ? 'bg-brass-50 text-brass-700 border-brass-500 ring-1 ring-brass-500'
                          : 'bg-white text-ink-700 border-gray-200 hover:border-brass-300'
                      }`}
                    >
                      <i className={`${heating === 'ja' ? 'ri-checkbox-circle-fill text-brass-500' : 'ri-checkbox-blank-circle-line text-gray-400'} text-base`}></i>
                      Ja, graag informatie
                    </button>
                    <button
                      type="button"
                      onClick={() => setHeating('nee')}
                      className={`flex items-center justify-center gap-2 px-4 py-3 sm:py-4 rounded-lg text-xs sm:text-sm font-medium transition-all border whitespace-nowrap ${
                        heating === 'nee'
                          ? 'bg-brass-50 text-brass-700 border-brass-500 ring-1 ring-brass-500'
                          : 'bg-white text-ink-700 border-gray-200 hover:border-brass-300'
                      }`}
                    >
                      <i className={`${heating === 'nee' ? 'ri-close-circle-fill text-brass-500' : 'ri-close-circle-line text-gray-400'} text-base`}></i>
                      Nee, alleen vloer
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => canGoNext && setStep(2)}
                  disabled={!canGoNext}
                  className={`w-full py-3.5 sm:py-4 rounded-xl text-sm sm:text-base font-semibold transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
                    canGoNext
                      ? 'bg-brass-500 hover:bg-brass-600 text-white hover:shadow-lg'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Gratis Offerte Aanvragen
                  <ArrowRight size={18} />
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="flex items-center gap-1 text-xs sm:text-sm text-ink-500 hover:text-brass-600 transition-colors mb-2"
                >
                  <ArrowLeft size={14} />
                  Terug
                </button>

                <h3 className="text-lg sm:text-xl font-semibold text-ink-900">Contactgegevens</h3>
                <p className="text-xs sm:text-sm text-ink-600 -mt-3">
                  Waar mogen we uw offerte naartoe sturen?
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-ink-900 mb-1">
                      Voornaam <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstname"
                      value={firstname}
                      onChange={(e) => setFirstname(e.target.value)}
                      placeholder="bijv. Jan"
                      required
                      className="w-full px-3 sm:px-4 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brass-500 focus:ring-1 focus:ring-brass-500 outline-none transition-all bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-semibold text-ink-900 mb-1">
                      Achternaam <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastname"
                      value={lastname}
                      onChange={(e) => setLastname(e.target.value)}
                      placeholder="bijv. de Vries"
                      required
                      className="w-full px-3 sm:px-4 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brass-500 focus:ring-1 focus:ring-brass-500 outline-none transition-all bg-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-ink-900 mb-1">
                    E-mailadres <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john@example.com"
                    required
                    className="w-full px-3 sm:px-4 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brass-500 focus:ring-1 focus:ring-brass-500 outline-none transition-all bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-ink-900 mb-1">
                    Telefoonnummer <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="06 12345678"
                    required
                    className="w-full px-3 sm:px-4 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brass-500 focus:ring-1 focus:ring-brass-500 outline-none transition-all bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-ink-900 mb-1">
                    Postcode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="postcode"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    placeholder="1234 AB"
                    required
                    className="w-full px-3 sm:px-4 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brass-500 focus:ring-1 focus:ring-brass-500 outline-none transition-all bg-white"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-ink-900 mb-1">
                    Opmerkingen (optioneel)
                  </label>
                  <textarea
                    name="remarks"
                    value={remarks}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) setRemarks(e.target.value);
                    }}
                    rows={3}
                    placeholder="Eventuele extra wensen of vragen..."
                    className="w-full px-3 sm:px-4 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brass-500 focus:ring-1 focus:ring-brass-500 outline-none transition-all resize-none bg-white"
                  />
                  <p className="text-[10px] sm:text-xs text-ink-400 mt-1 text-right">{remarks.length}/500 tekens</p>
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit || submitting}
                  className={`w-full py-3.5 sm:py-4 rounded-xl text-sm sm:text-base font-semibold transition-all flex items-center justify-center gap-2 whitespace-nowrap ${
                    canSubmit && !submitting
                      ? 'bg-brass-500 hover:bg-brass-600 text-white hover:shadow-lg'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Verzenden...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Offerte Aanvragen
                    </>
                  )}
                </button>

                <p className="text-[10px] sm:text-xs text-ink-400 text-center -mt-2">
                  Door dit formulier te verzenden, gaat u akkoord met onze privacyverklaring
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}